import { isHotkey, isTextEntryTarget, logger } from '@tunasong/models'
import { useGlobalCommands, useUserConfig } from '@tunasong/ui-lib'
import { useEffect } from 'react'

/**
 * Document-level keydown listener that dispatches global plugin commands from
 * `userConfig.hotkeys`.
 *
 * Mirror of `plugins/core/src/commands-hotkeys.tsx`, but:
 *  - element: `document.body` (not the editor's host element)
 *  - lookup: `getGlobalCommands` (not `runEditorCommand`)
 *
 * Editor commands keep priority for events that fire inside the editor: the
 * editor handler calls `preventDefault` + `stopPropagation` on matches, so
 * those events do not bubble to the document. Events that don't match an
 * editor command (or that fire outside the editor) bubble to this handler.
 *
 * The hook depends on `useUserConfig` (which needs `GlobalAppContext`) and
 * `useGlobalCommands` (which needs the plugin context from `DIContainer`),
 * so it MUST be mounted inside both providers. Use `<GlobalCommandHotkeys />`
 * rather than calling this hook from the bare `AppContainer` body — that
 * runs above the providers and throws "GlobalAppContext is not set".
 */
export const useGlobalCommandHotkeys = () => {
  const [hotkeys] = useUserConfig('hotkeys')
  const commands = useGlobalCommands({ text: '' })

  useEffect(() => {
    const handleKeydown = (ev: KeyboardEvent) => {
      // GH #1806: this listener is on `document.body`, so keydown from any
      // focused text control reaches it. Consuming those events prevents the
      // control's own handling of the key.
      if (isTextEntryTarget(ev.target)) {
        return
      }
      for (const { hotkey, commandId } of hotkeys) {
        if (typeof hotkey !== 'string' || !isHotkey(hotkey)(ev)) {
          continue
        }
        const command = commands.find(c => c.id === commandId)
        if (!command || typeof command.cmd !== 'function') {
          return
        }
        ev.preventDefault()
        ev.stopPropagation()
        const result = command.cmd({})
        if (result && typeof (result as Promise<unknown>).catch === 'function') {
          ;(result as Promise<unknown>).catch(err => {
            logger.error('Global command failed', { err, commandId })
          })
        }
        return
      }
    }

    document.body.addEventListener('keydown', handleKeydown)
    return () => {
      document.body.removeEventListener('keydown', handleKeydown)
    }
  }, [commands, hotkeys])
}
